import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql } from '../lib/api';
import { STORES_DETAIL_QUERY, UPSERT_STORE_MUTATION } from '../graphql/queries';

interface Store {
  id: string;
  name: string;
  seatCount: number | null;
  openingHours: string | null;
  openDate: string | null;
  airmateAkr: string | null;
  airregiRegisterIds: string[] | null;
  airregiBusinessDayStart: string | null;
  lunchAggregationEnd: string | null;
  dinnerAggregationEnd: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  dinnerStart: string | null;
  dinnerEnd: string | null;
  drinkCategoryKeywords: string[] | null;
}

interface Draft {
  id: string;
  name: string;
  seatCount: string;
  openingHours: string;
  openDate: string;
  airmateAkr: string;
  airregiRegisterIds: string;
  airregiBusinessDayStart: string;
  lunchAggregationEnd: string;
  dinnerAggregationEnd: string;
  lunchStart: string;
  lunchEnd: string;
  dinnerStart: string;
  dinnerEnd: string;
  drinkCategoryKeywords: string;
}

const emptyDraft: Draft = {
  id: '',
  name: '',
  seatCount: '',
  openingHours: '',
  openDate: '',
  airmateAkr: '',
  airregiRegisterIds: '',
  airregiBusinessDayStart: '05:00',
  lunchAggregationEnd: '',
  dinnerAggregationEnd: '29:00',
  lunchStart: '',
  lunchEnd: '',
  dinnerStart: '',
  dinnerEnd: '',
  drinkCategoryKeywords: '',
};

const parseKeywords = (s: string): string[] | null => {
  const list = s
    .split(/[,、\n]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
  return list.length > 0 ? list : null;
};

const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const StoresPage = () => {
  const qc = useQueryClient();
  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isEditing = editingId !== null;

  const mutation = useMutation({
    mutationFn: (vars: {
      id: string;
      name: string;
      seatCount?: number | null;
      openingHours?: string | null;
      openDate?: string | null;
      airmateAkr?: string | null;
      airregiRegisterIds?: string[] | null;
      airregiBusinessDayStart?: string | null;
      lunchAggregationEnd?: string | null;
      dinnerAggregationEnd?: string | null;
      lunchStart?: string | null;
      lunchEnd?: string | null;
      dinnerStart?: string | null;
      dinnerEnd?: string | null;
      drinkCategoryKeywords?: string[] | null;
    }) => gql<{ upsertStore: Store }>(UPSERT_STORE_MUTATION, vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['storesDetail'] });
      void qc.invalidateQueries({ queryKey: ['stores'] });
      setDraft(emptyDraft);
      setEditingId(null);
    },
  });

  const startEdit = (s: Store) => {
    setEditingId(s.id);
    setDraft({
      id: s.id,
      name: s.name,
      seatCount: s.seatCount?.toString() ?? '',
      openingHours: s.openingHours ?? '',
      openDate: s.openDate ?? '',
      airmateAkr: s.airmateAkr ?? '',
      airregiRegisterIds: (s.airregiRegisterIds ?? []).join(', '),
      airregiBusinessDayStart: s.airregiBusinessDayStart ?? '05:00',
      lunchAggregationEnd: s.lunchAggregationEnd ?? (s.lunchStart ? s.dinnerStart ?? '' : ''),
      dinnerAggregationEnd: s.dinnerAggregationEnd ?? '29:00',
      lunchStart: s.lunchStart ?? '',
      lunchEnd: s.lunchEnd ?? '',
      dinnerStart: s.dinnerStart ?? '',
      dinnerEnd: s.dinnerEnd ?? '',
      drinkCategoryKeywords: (s.drinkCategoryKeywords ?? []).join(', '),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const submit = () => {
    if (!isEditing && !ID_PATTERN.test(draft.id)) {
      alert('店舗ID は 半角英数・ハイフン・アンダースコアのみ、64文字以内で入力してください。');
      return;
    }
    if (!draft.name.trim()) {
      alert('店舗名は必須です。');
      return;
    }
    const seatCount = draft.seatCount.trim() ? Number(draft.seatCount) : null;
    if (seatCount !== null && (!Number.isInteger(seatCount) || seatCount < 1 || seatCount > 2147483647)) {
      alert('座席数は1以上の整数で入力してください。');
      return;
    }
    const airregiRegisterIds = parseKeywords(draft.airregiRegisterIds);
    if (airregiRegisterIds?.some((id) => !/^[A-Za-z0-9_-]{1,64}$/.test(id))) {
      alert('AirレジのレジIDは半角英数字・ハイフン・アンダースコアで入力してください。');
      return;
    }
    if (stores.some((store) => store.id !== draft.id && airregiRegisterIds?.some((id) => store.airregiRegisterIds?.includes(id)))) {
      alert('同じAirレジのレジIDが別の店舗にも登録されています。');
      return;
    }
    if (!/^(?:[01]\d|2[0-3]):00$/.test(draft.airregiBusinessDayStart)) {
      alert('Airレジの営業日切替は00:00～23:00の毎時で設定してください。');
      return;
    }
    const aggregationMinutes = (value: string): number | null => {
      const match = /^(\d{1,2}):([0-5]\d)$/.exec(value);
      if (!match) return null;
      const hour = Number(match[1]);
      return hour <= 30 ? hour * 60 + Number(match[2]) : null;
    };
    const lunchEnd = draft.lunchStart ? aggregationMinutes(draft.lunchAggregationEnd) : null;
    const dinnerStart = aggregationMinutes(draft.dinnerStart);
    const dinnerEnd = aggregationMinutes(draft.dinnerAggregationEnd);
    const dayChange = aggregationMinutes(draft.airregiBusinessDayStart)! + 24 * 60;
    if (draft.lunchStart && (lunchEnd === null || dinnerStart === null || lunchEnd > dinnerStart)) {
      alert('ランチ集計終点はディナー開始時刻以前に設定してください。');
      return;
    }
    if (dinnerStart !== null && (dinnerEnd === null || dinnerEnd <= dinnerStart || dinnerEnd > dayChange)) {
      alert('ディナー集計終点はディナー開始後、翌営業日切替時刻以前に設定してください。');
      return;
    }
    if (draft.dinnerEnd && !/^(?:[01]\d|2\d|30):[0-5]\d$/.test(draft.dinnerEnd)) {
      alert('ディナー終了はHH:mm形式で入力してください（例：23:00、24:00）。');
      return;
    }
    mutation.mutate({
      id: draft.id,
      name: draft.name.trim(),
      seatCount,
      openingHours: draft.openingHours.trim() || null,
      openDate: draft.openDate || null,
      airmateAkr: draft.airmateAkr.trim() || null,
      airregiRegisterIds: airregiRegisterIds ?? [],
      airregiBusinessDayStart: draft.airregiBusinessDayStart,
      lunchAggregationEnd: draft.lunchStart ? draft.lunchAggregationEnd : null,
      dinnerAggregationEnd: draft.dinnerAggregationEnd,
      lunchStart: draft.lunchStart.trim() || null,
      lunchEnd: draft.lunchEnd.trim() || null,
      dinnerStart: draft.dinnerStart.trim() || null,
      dinnerEnd: draft.dinnerEnd.trim() || null,
      drinkCategoryKeywords: parseKeywords(draft.drinkCategoryKeywords),
    });
  };

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '1.4rem', marginBottom: '1rem' }}>店舗マスタ</h1>

      <section style={card}>
        <h2 style={sectionTitle}>{isEditing ? `編集：${editingId}` : '新規登録'}</h2>
        <p style={hint}>
          店舗ID は CSV の店舗自動判定に使われます。一度設定すると変更できないため、
          英数字で短く意味のある値（例：<code>loop-hamburg</code>、<code>koen</code>）を推奨します。
        </p>
        <div style={{ display: 'grid', gap: '0.6rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <label>
            店舗ID（半角英数・記号）
            <input
              type="text"
              value={draft.id}
              disabled={isEditing}
              onChange={(e) => setDraft({ ...draft, id: e.target.value })}
              placeholder="loop-hamburg"
              style={{ ...input, background: isEditing ? '#f1f3f5' : '#fff' }}
            />
          </label>
          <label>
            店舗名
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="ループ ハンバーグ 西本町店"
              style={input}
            />
          </label>
          <label>
            座席数（席）
            <input
              type="number"
              min="1"
              max="2147483647"
              step="1"
              value={draft.seatCount}
              onChange={(e) => setDraft({ ...draft, seatCount: e.target.value })}
              placeholder="19"
              style={input}
            />
          </label>
          <label style={{ gridColumn: '1 / 3' }}>
            営業時間（任意）
            <input
              type="text"
              value={draft.openingHours}
              onChange={(e) => setDraft({ ...draft, openingHours: e.target.value })}
              placeholder="11:00-15:00 / 18:00-23:00（月曜定休）"
              style={input}
            />
          </label>
          <label>
            開店日（任意）
            <input
              type="date"
              value={draft.openDate}
              onChange={(e) => setDraft({ ...draft, openDate: e.target.value })}
              style={input}
            />
          </label>
          <label>
            Airメイト AKR（任意）
            <input
              type="text"
              value={draft.airmateAkr}
              onChange={(e) => setDraft({ ...draft, airmateAkr: e.target.value })}
              placeholder="AKR6813242569"
              style={input}
            />
          </label>
          <label>
            Airレジ レジID（カンマ区切り）
            <input
              type="text"
              value={draft.airregiRegisterIds}
              onChange={(e) => setDraft({ ...draft, airregiRegisterIds: e.target.value })}
              placeholder="0007"
              style={input}
            />
          </label>
          <label>
            Airレジ 営業日切替時刻
            <input
              type="time"
              step="3600"
              value={draft.airregiBusinessDayStart}
              onChange={(e) => setDraft({ ...draft, airregiBusinessDayStart: e.target.value })}
              style={input}
            />
            <span style={hintInline}>Airレジ「レジ基本設定」の値と合わせます。現在の2店舗は05:00です。</span>
          </label>
          <label>
            ランチ集計終点（HH:mm、ランチ営業のある店）
            <input
              value={draft.lunchAggregationEnd}
              onChange={(e) => setDraft({ ...draft, lunchAggregationEnd: e.target.value })}
              placeholder="18:00"
              style={input}
            />
            <span style={hintInline}>ディナー開始時刻までの来店分をランチに集計します。</span>
          </label>
          <label>
            ディナー集計終点（HH:mm）
            <input
              value={draft.dinnerAggregationEnd}
              onChange={(e) => setDraft({ ...draft, dinnerAggregationEnd: e.target.value })}
              placeholder="29:00"
              style={input}
            />
            <span style={hintInline}>翌日05:00は「29:00」と入力します。Airレジの営業日切替時刻まで設定できます。</span>
          </label>
          <label>
            ランチ開始（HH:mm、任意）
            <input
              type="time"
              value={draft.lunchStart}
              onChange={(e) => setDraft({ ...draft, lunchStart: e.target.value })}
              style={input}
            />
          </label>
          <label>
            ランチ終了（HH:mm、任意）
            <input
              type="time"
              value={draft.lunchEnd}
              onChange={(e) => setDraft({ ...draft, lunchEnd: e.target.value })}
              style={input}
            />
          </label>
          <label>
            ディナー開始（HH:mm）
            <input
              type="time"
              value={draft.dinnerStart}
              onChange={(e) => setDraft({ ...draft, dinnerStart: e.target.value })}
              style={input}
            />
          </label>
          <label>
            ディナー終了（HH:mm）
            <input
              type="text"
              inputMode="numeric"
              value={draft.dinnerEnd}
              onChange={(e) => setDraft({ ...draft, dinnerEnd: e.target.value })}
              placeholder="24:00"
              style={input}
            />
          </label>
          <label style={{ gridColumn: '1 / 3' }}>
            ドリンク判定キーワード（カンマ／改行区切り、任意）
            <textarea
              rows={2}
              value={draft.drinkCategoryKeywords}
              onChange={(e) => setDraft({ ...draft, drinkCategoryKeywords: e.target.value })}
              placeholder="ドリンク, アルコール, ビール, ハイボール, ワイン, 日本酒, 焼酎, ソフトドリンク, サワー"
              style={{ ...input, fontFamily: 'inherit' }}
            />
            <span style={hintInline}>
              カテゴリ名にこれらのキーワードを含む商品を「ドリンク注文数」として集計します。空欄なら既定値（ドリンク／アルコール／酒など）を使用します。
            </span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={submit}
            style={primaryBtn}
          >
            {mutation.isPending ? '保存中…' : isEditing ? '上書き保存' : '登録'}
          </button>
          {isEditing && (
            <button type="button" onClick={cancelEdit} style={secondaryBtn}>
              キャンセル
            </button>
          )}
        </div>
        {mutation.isSuccess && (
          <p style={{ color: '#006d8d', marginTop: '0.5rem' }}>保存しました</p>
        )}
        {mutation.isError && (
          <p style={{ color: '#aa0b56', marginTop: '0.5rem' }}>
            エラー：{(mutation.error as Error).message}
          </p>
        )}
      </section>

      <section style={card}>
        <h2 style={sectionTitle}>登録済み店舗</h2>
        {storesQ.isLoading ? (
          <p style={muted}>読込中…</p>
        ) : stores.length === 0 ? (
          <p style={muted}>まだ登録されていません。上のフォームから登録してください。</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>店舗ID</th>
                  <th style={th}>店舗名</th>
                  <th style={th}>座席数</th>
                  <th style={th}>ランチ</th>
                  <th style={th}>ディナー</th>
                  <th style={th}>ドリンク判定</th>
                  <th style={th}>AKR</th>
                  <th style={th}>Airレジ レジID</th>
                  <th style={th}>営業日切替</th>
                  <th style={th}>集計終点（ランチ／ディナー）</th>
                  <th style={th}>操作</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((s) => (
                  <tr key={s.id}>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{s.id}</td>
                    <td style={td}>{s.name}</td>
                    <td style={td}>{s.seatCount != null ? `${s.seatCount}席` : '—'}</td>
                    <td style={td}>
                      {s.lunchStart && s.lunchEnd ? `${s.lunchStart}-${s.lunchEnd}` : '—'}
                    </td>
                    <td style={td}>
                      {s.dinnerStart && s.dinnerEnd ? `${s.dinnerStart}-${s.dinnerEnd}` : '—'}
                    </td>
                    <td style={{ ...td, fontSize: '0.78rem', maxWidth: 200 }}>
                      {(s.drinkCategoryKeywords ?? []).length > 0
                        ? (s.drinkCategoryKeywords ?? []).join(', ')
                        : '（既定）'}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {s.airmateAkr ?? '-'}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {(s.airregiRegisterIds ?? []).join(', ') || '—'}
                    </td>
                    <td style={td}>{s.airregiBusinessDayStart ?? '05:00'}</td>
                    <td style={td}>{s.lunchAggregationEnd ?? '—'} ／ {s.dinnerAggregationEnd ?? '29:00'}</td>
                    <td style={td}>
                      <button type="button" onClick={() => startEdit(s)} style={linkBtn}>
                        編集
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const hint: React.CSSProperties = {
  background: '#f1f3f5',
  border: '1px solid #dee2e6',
  color: '#495057',
  padding: '0.6rem 0.8rem',
  borderRadius: 6,
  fontSize: '0.82rem',
  marginBottom: '0.75rem',
};
const hintInline: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  color: '#868e96',
  fontSize: '0.75rem',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.9rem' };
const input: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  width: '100%',
};
const primaryBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: 6,
  border: '1px solid #aa0b56',
  background: '#aa0b56',
  color: '#fff',
  cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
  color: '#495057',
  cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: 4,
  border: '1px solid #dee2e6',
  background: '#fff',
  color: '#1864ab',
  cursor: 'pointer',
  fontSize: '0.8rem',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
};
